import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { deviceTokensAPI } from '../../utils/api';
import {
  LEGACY_ENTRY,
  disableBiometricLogin,
  enabledEmail,
  getBiometricAvailability,
  isNativeApp,
  isNetworkError,
  storeDeviceToken,
  unlockDeviceToken,
} from '../../utils/biometricAuth';
import './Biometric.css';

/** Auto-prompt at most once per app launch; a session store is cleared on cold start. */
const AUTO_PROMPT_KEY = 'ncad.biometric.autoPrompted';

/** Said whenever the saved sign-in is gone for good and only a password will do. */
const USE_PASSWORD =
  'Your saved sign-in is no longer valid — your password may have changed. Please sign in with your password.';

const alreadyAutoPrompted = () => {
  try {
    return sessionStorage.getItem(AUTO_PROMPT_KEY) === '1';
  } catch {
    return true;
  }
};

const markAutoPrompted = () => {
  try {
    sessionStorage.setItem(AUTO_PROMPT_KEY, '1');
  } catch { /* nothing to remember means we simply will not re-prompt */ }
};

/**
 * "Sign in with Face ID" on the login screen. Renders nothing off-native, when
 * the account never enabled it, or when biometrics stopped being available.
 *
 * What the secure store holds is a device token, not a password, and the server
 * ROTATES it on every use: the token that just signed us in is dead by the time
 * the response arrives. So the order is fixed — exchange, STORE the replacement,
 * and only then commit the session, because committing it redirects and unmounts
 * this component.
 *
 * If the replacement cannot be stored the sign-in still stands, but biometric
 * sign-in is turned off here AND the unstorable token is revoked on the server,
 * rather than left alive with nobody holding it. The session is then held back
 * until the user has acknowledged the notice, since navigating away would take
 * the notice with it.
 *
 * The login card has room for ONE message on a 375x667 phone, so this block and
 * the form take turns: `onAttempt` lets the form clear its error when an unlock
 * starts, and `quiet` (the form is showing an error of its own) clears ours.
 * `onHold` tells the page the user is signed in and only has a notice left to
 * acknowledge, so it can put the password form away.
 */
export default function BiometricUnlock({ onSignedIn, onAttempt, onHold, quiet = false }) {
  const { exchangeDeviceToken, completeDeviceSignIn } = useAuth();
  const [label, setLabel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  // A signed-in user waiting on "Continue" after the store-failure notice.
  const [heldUser, setHeldUser] = useState(null);
  const promptedRef = useRef(false);
  const shownRef = useRef(false);
  const continueRef = useRef(null);

  useEffect(() => {
    if (!isNativeApp() || !enabledEmail()) return undefined;
    let live = true;
    getBiometricAvailability().then(({ available, label: type }) => {
      if (live && available) setLabel(type);
    });
    return () => { live = false; };
  }, []);

  const runUnlock = useCallback(async (type) => {
    setBusy(true);
    setMessage(null);
    onAttempt?.();
    let stored;
    try {
      stored = await unlockDeviceToken(type);
    } catch (error) {
      if (error?.code === LEGACY_ENTRY) {
        // An entry from the build that saved a password. It has just been
        // deleted, unread and unsent.
        setLabel(null);
        setMessage({ tone: 'alert', text: USE_PASSWORD });
      } else {
        setMessage({ tone: 'status', text: `${type} sign-in was not completed. Enter your password to continue.` });
      }
      setBusy(false);
      return;
    }
    let exchanged;
    try {
      exchanged = await exchangeDeviceToken(stored.token);
    } catch (error) {
      if (isNetworkError(error)) {
        setMessage({ tone: 'alert', text: 'Could not reach the server. Try again, or sign in with your password.' });
      } else {
        await disableBiometricLogin();
        setLabel(null);
        setMessage({ tone: 'alert', text: USE_PASSWORD });
      }
      setBusy(false);
      return;
    }

    const { user, deviceToken } = exchanged;
    const saved = await storeDeviceToken(stored.email, deviceToken?.token, deviceToken?.id)
      .catch(() => false);
    if (saved) {
      await completeDeviceSignIn(user);
      onSignedIn(user);
      setBusy(false);
      return;
    }

    // The copy on this device is the dead one and the live one could not be
    // kept. Forget the feature locally and hand the live token back, so it is
    // not left valid with no holder. The session JWT from the exchange is what
    // authorises the revoke.
    await disableBiometricLogin();
    if (deviceToken?.id) {
      try { await deviceTokensAPI.revoke(deviceToken.id); } catch { /* it will expire */ }
    }
    setLabel(null);
    setHeldUser(user);
    onHold?.();
    setMessage({
      tone: 'alert',
      text: `You are signed in, but ${type} sign-in has been turned off: this device could not save its new sign-in key. You can turn it back on from your profile.`,
    });
    setBusy(false);
  }, [exchangeDeviceToken, completeDeviceSignIn, onSignedIn, onAttempt, onHold]);

  const continueSignedIn = async () => {
    await completeDeviceSignIn(heldUser);
    onSignedIn(heldUser);
  };

  useEffect(() => {
    if (heldUser) continueRef.current?.focus();
  }, [heldUser]);

  useEffect(() => {
    if (quiet && !heldUser) setMessage(null);
  }, [quiet, heldUser]);

  useEffect(() => {
    if (!label || promptedRef.current || alreadyAutoPrompted()) return;
    promptedRef.current = true;
    markAutoPrompted();
    runUnlock(label);
  }, [label, runUnlock]);

  // Once this block has appeared it stays, so the two live regions below are
  // already in the document when text is put into one of them.
  if (label || message) shownRef.current = true;
  if (!shownRef.current) return null;

  return (
    <div className="biometric-unlock">
      {label && (
        <button
          type="button"
          className="biometric-unlock__button"
          onClick={() => runUnlock(label)}
          disabled={busy}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
            <path d="M9 10v1M15 10v1M9 15c.9.7 1.9 1 3 1s2.1-.3 3-1" />
          </svg>
          {busy ? `Waiting for ${label}…` : `Sign in with ${label}`}
        </button>
      )}
      {/* Two fixed live regions, one populated at a time. Flipping `role` on a
          single node is not reliably re-announced by screen readers. */}
      <p className="biometric-unlock__message" role="status">
        {message?.tone === 'status' ? message.text : null}
      </p>
      <p className="biometric-unlock__message" role="alert">
        {message?.tone === 'alert' ? message.text : null}
      </p>
      {heldUser && (
        <button
          type="button"
          className="biometric-unlock__button"
          ref={continueRef}
          onClick={continueSignedIn}
        >
          Continue to NCAD Bookings
        </button>
      )}
    </div>
  );
}
